// A report assembled from what the page holds, in the shape of
// `ReportRequest` (api/openapi.yaml), group by group so the panel can show
// each and the person can remove any of them (ADR-0518 2a).
import type { components } from "../api/schema";
import type { Captured } from "./capture";

export type ReportRequest = components["schemas"]["ReportRequest"];
export type Kind = ReportRequest["kind"];

/** The groups a person may keep or remove, in the order the panel shows them. */
export const GROUPS = ["page", "browser", "build", "scope", "problem", "calls", "errors"] as const;
export type Group = (typeof GROUPS)[number];
/** The groups a `feature` report leaves out until the person adds them (2a). */
export const DIAGNOSTICS: readonly Group[] = ["problem", "calls", "errors"];

/** The route as the router holds it, **every query value dropped** and its
 * keys kept: `/accept?token=…` carries an invitation token in its value. */
export function pathWithoutValues(pathname: string, search: string): string {
  const keys = [...new URLSearchParams(search).keys()];
  return keys.length ? `${pathname}?${keys.map((k) => `${encodeURIComponent(k)}=`).join("&")}` : pathname;
}

export interface PageFacts {
  pathname: string;
  search: string;
  title: string;
  scope: { org?: { id: string; name?: string }; customer?: { id: string; name?: string }; site?: { id: string; name?: string }; role?: string };
}

function ref(r: { id: string; name?: string } | undefined) {
  return r ? { id: r.id.slice(0, 64), ...(r.name ? { name: r.name.slice(0, 255) } : {}) } : undefined;
}

/** Each group's content, from the page and from what was captured. A group
 * with nothing in it is absent, so the panel does not offer an empty one. */
export function groups(facts: PageFacts, captured: Captured): Partial<Pick<ReportRequest, Group>> {
  const out: Partial<Pick<ReportRequest, Group>> = {
    page: { path: pathWithoutValues(facts.pathname, facts.search).slice(0, 2048), title: facts.title.slice(0, 300) },
    browser: {
      user_agent: navigator.userAgent.slice(0, 512),
      platform: (navigator.platform || "").slice(0, 100),
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      device_pixel_ratio: window.devicePixelRatio || 1,
      language: (navigator.language || "").slice(0, 40),
      time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone?.slice(0, 80),
    },
    build: { version: __ZENITH_VERSION__.slice(0, 80), commit: __ZENITH_COMMIT__.slice(0, 80) },
  };
  const s = facts.scope;
  if (s.org || s.customer || s.site || s.role) {
    out.scope = { organization: ref(s.org), customer: ref(s.customer), site: ref(s.site), ...(s.role ? { role: s.role.slice(0, 40) } : {}) };
  }
  if (captured.problem) out.problem = captured.problem;
  if (captured.calls.length) out.calls = captured.calls;
  if (captured.errors.length) out.errors = captured.errors;
  return out;
}

/** The request the panel sends: the person's words and the groups they kept. */
export function request(kind: Kind, text: string, available: Partial<Pick<ReportRequest, Group>>, kept: ReadonlySet<Group>, screenshot?: string): ReportRequest {
  const body: ReportRequest = { kind, text };
  for (const g of GROUPS) {
    if (kept.has(g) && available[g] !== undefined) Object.assign(body, { [g]: available[g] });
  }
  if (screenshot) body.screenshot_png = screenshot;
  return body;
}

/** The groups kept when the panel opens: all of them for a bug, and none of
 * the diagnostics for a feature request (2a). */
export function keptByDefault(kind: Kind): Set<Group> {
  return new Set(GROUPS.filter((g) => kind === "bug" || !DIAGNOSTICS.includes(g)));
}

/** A Blob as the base64 the contract's `format: byte` names. */
export async function base64(blob: Blob): Promise<string> {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  let bin = "";
  for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(bin);
}
